import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import DetailItem from '../../../../Views/Pages/Employer/employer-details/basic-details/DetailItem';
import deleteBlackIcon from '../../../../assets/img/svg/delete-black.svg';
import { useDispatch, useSelector } from 'react-redux';
import { deleteFollowUp, fetchProcessingFollowUpList, saveFollowUp } from '../../../../redux/Slices/processing/ProcessingSlice';
import { fetchTemplateNameByFilter } from '../../../../redux/Slices/settings/templates/TemplateSlice';
import { showSuccessOrError } from '../../../../services/commonService';
import CustomSpinner from '../../../ReusableComponents/CustomSpinner';
import SubmitSpinnerButton from '../../../ReusableComponents/SubmitSpinnerButton';
import ConfirmApproval from '../confirm-approval/ConfirmApproval';
import { Link } from 'react-router-dom';
import translation from '../../../../utils/translation';
import { followUpFormState } from '../../../ReusableComponents/form-states';
import routes from '../../../../Views/Pages/routes/routesConfig';

function ProcessingFollowUpPopupView({ onClose, isEditMode = false, followUpId, onRefresh }) {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState(!isEditMode);
  const [isConfirmApproval, setIsConfirmApproval] = useState(false);
  const [followUpDetails, setFollowUpDetails] = useState({});
  const { templateNameList } = useSelector((state) => state.templates);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: followUpFormState
  });

  useEffect(() => {
    setIsLoadingContent(true);
    dispatch(fetchProcessingFollowUpList(followUpId)).then((res) => {
      if (res?.payload?.data) {
        setFollowUpDetails(res.payload.data);
      }
      setIsLoadingContent(false);
    }); 
    dispatch(fetchTemplateNameByFilter({
      category_id: 56,  // has to send static, is being managed in backend
      module_key: 'processing',
    }));
  }, [dispatch]);


  useEffect(() => {
    if (followUpDetails) {
      setValue('follow_up_id', followUpDetails.id);

      const selectedTemplate = templateNameList.filter((item) => item.template_name === followUpDetails.title);
      // If a matching template was found, use it, otherwise set the title directly
      if (selectedTemplate && selectedTemplate.length > 0) {
        setValue('title', JSON.stringify(selectedTemplate[0])); // Use [0] to get the first item
      } else {
        // If no template found, you may want to set the raw title or handle this case
        setValue('title', '');
      }

      setValue('description', followUpDetails.description);
      setValue('follow_up_date', followUpDetails.follow_up_date);
      setValue('follow_up_time', followUpDetails.follow_up_time);
    }
  }, [followUpDetails, setValue]);

  const toggleEditMode = () => {
    setViewMode(!viewMode);
  };

  const onSubmit = (data) => {
    const parsedTitle = JSON.parse(data.title);
    const formattedFollowUpTime = data.follow_up_time
      ? (data.follow_up_time.includes(':') && !data.follow_up_time.endsWith(':00')
        ? `${data.follow_up_time}:00`
        : data.follow_up_time)
      : null;
    const payload = {
      ...data,
      title: parsedTitle.template_name,
      template_content_id: parsedTitle.id,
      contract_id: followUpDetails?.contract_id,
      follow_up_time: formattedFollowUpTime,
      follow_up_id: followUpId,
    }
    setSubmitStatus('loading');
    console.log("Form submitted with data:", payload);
    dispatch(saveFollowUp(payload)).then((res) => {
      showSuccessOrError(res);
      setSubmitStatus('success');
      if (!res?.error) {
        if (res?.payload?.data) {
          setFollowUpDetails(res.payload.data);
        }
        setViewMode(true);
        if (onRefresh) {
          onRefresh();
        }
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  };

  const onDeleteClick = (e) => {
    e.preventDefault();
    setIsConfirmApproval(true);
  }

  const handleConfirmation = () => {
    dispatch(deleteFollowUp({follow_up_id:followUpId})).then((res) => {
      showSuccessOrError(res);
      if(!res.error) {
        onClose();
      }
    });
  }

  return (
    <React.Fragment>
      {isConfirmApproval ? <ConfirmApproval 
      description="Are you sure you want to delete this follow up?"
      onClose={() => setIsConfirmApproval(false)} handleConfirmation={handleConfirmation} />
      : <div
      className="modal fade modalCustom addAddress show"
      tabIndex={-1}
      aria-labelledby="exampleModalCenterTitle"
      style={{
        display: "block",
        background: "rgba(0, 0, 0, 0.7)",
      }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog" role="document" style={{ maxWidth: '665px' }}>
        <div className="modal-content br20">
          <div className="modal-body p32">
            <div className="d-flex justify-content-between align-items-center mb24">
              <h5 className="fz20 font-roboto fw-500 black">
                {translation('key_processing_follow_up')}
                <Link to={routes?.processingDetails.replace(':contract_id', followUpDetails?.contract_id)} className="d-block m-0 fz16 fw-400 mt-1">
                  {followUpDetails?.contractModel?.contract_number}
                </Link>
              </h5>
              <div className="d-flex align-items-center">
                {viewMode && (
                  <>
                  <Link to="#" onClick={onDeleteClick}>
                     <img src={deleteBlackIcon} alt="delete" className="cursor-pointer mr15" />
                  </Link>
                    
                    <i className="ri-pencil-fill mr-2 cursor-pointer blue fz20"
                      onClick={toggleEditMode}></i>
                  </>
                )}
                <i className="ri-close-line cursor-pointer grey2 fz20" onClick={onClose}></i>
              </div>
            </div>
            {isLoadingContent ? <CustomSpinner /> :
              <>
                {viewMode ? (
                  <div className="view-form">
                    <DetailItem
                          title={ translation('key_employer_name') }
                      desc={followUpDetails?.contractModel?.employersModel?.employer_name}
                      columnSize='mb24'
                    />
                    <DetailItem
                      title={translation('key_worker_name')}
                      desc={followUpDetails?.contractModel?.workersModel?.worker_name}
                      columnSize='mb24'
                    />

                    <div className="form-row">
                      <div className="col">
                        <DetailItem
                          title={translation('key_date')}
                          desc={formatDate(followUpDetails?.follow_up_date)}
                          columnSize='mb24'
                        />
                      </div>
                      <div className="col">
                        <DetailItem
                          title={translation('key_time')}
                          desc={followUpDetails?.follow_up_time}
                          columnSize='mb24'
                        />
                      </div>
                    </div>
                    <DetailItem
                      title={translation('key_follow_up_notes')}
                      desc={followUpDetails?.description}
                      columnSize='mb24'
                    />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <DetailItem
                            title={ translation('key_employer_name') }
                      desc={followUpDetails?.contractModel?.employersModel?.employer_name}
                      columnSize='mb24'
                    />
                    <DetailItem
                      title={translation('key_worker_name')}
                      desc={followUpDetails?.contractModel?.workersModel?.worker_name}
                      columnSize='mb24'
                    />

                    <div className="form-group mb-3">
                      <label className="form-label required">{translation('key_template_name')}</label>
                      <select
                        className="form-control ht44"
                        id="title"
                        {...register("title", {
                          required: "Please select a title",
                        })}
                      >
                        <option value="">{translation('key_please_select')}</option>
                        {!templateNameList?.error &&
                          templateNameList?.map((item) => (
                            <option key={item.id} value={JSON.stringify(item)}>
                              {item.template_name}
                            </option>
                          ))}
                      </select>
                      {errors.title && (
                        <small className="text-danger">
                          {errors.title.message}
                        </small>
                      )}
                    </div>

                    <div className="form-group mb-4">
                      <label className="form-label required">{translation('key_follow_up_notes')} </label>
                      <textarea
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        rows="3"
                        {...register("description", { required: true })}
                      ></textarea>
                      {errors.description && <div className="invalid-feedback">{translation('key_this_field_is_required')}</div>}
                    </div>

                    <div className="row mb-3">
                      <div className="col">
                        <div className="form-group">
                                <label className="form-label required">{ translation('key_date') }</label>
                          <input
                            type="date"
                            className={`form-control ${errors.follow_up_date ? 'is-invalid' : ''}`}
                            {...register("follow_up_date", { required: true })}
                          />
                          {errors.follow_up_date && <div className="invalid-feedback">{translation('key_this_field_is_required')}</div>}
                        </div>
                      </div>
                      <div className="col">
                        <div className="form-group">
                          <label className="form-label required">{translation('key_time')}</label>
                          <input
                            type="time"
                            className={`form-control ${errors.follow_up_time ? 'is-invalid' : ''}`}
                            {...register("follow_up_time", { required: true })}
                          />
                          {errors.follow_up_time && <div className="invalid-feedback">{translation('key_this_field_is_required')}</div>}
                        </div>
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col-12 footer-btns text-right'>
                        <button type="button" className="btn btn-outline mr-2" onClick={() => setViewMode(true)}>{translation('key_cancel')}</button>
                        <SubmitSpinnerButton status={submitStatus} submitText={translation('key_save')} />
                      </div>
                    </div>
                  </form>
                )}
              </>
            }

          </div>
        </div>
      </div>
    </div>
    }
     
    </React.Fragment>
    
  );
}

ProcessingFollowUpPopupView.propTypes = {
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  followUpId: PropTypes.number,
  onRefresh: PropTypes.func
};

export default ProcessingFollowUpPopupView;